
import { resolveItems, getItemsOperation } from '../operations/getItems';
import { KeyFacet, Key } from '../models/KeyFacet';
import { Facet, IFacet, pack } from '../models/Facet';
import {
    createAddon,
    registerOperationHandler,
    createBatchedItemsOperationHandler,
    IBatchOperationPayload,
    IOperationHandlerThunkAction
} from './registerOperationHandler';
import { IItemCacheDispatch, IItemCacheThunkAction } from '../store/actions/ItemCacheActions';
import { IItemCache } from '../models/IItemCacheObserver';
import { ControlFacet, IControl, ControlTypeKey, controlTypeKeyFacet } from '../models/controls/ControlFacet';
import { registerPermanentItemDemands } from '../operations/pruneItems';
import { readItems } from '../operations/readItems';
import { ItemResolver } from '../models/items/ItemResolver';

function alreadyActivated(): void {
    // The activator for an already-activated control handler does not need to do anything.
}

/**
 * Facet which stores whether or not any component has attempted to consume a given control handler.
 */
const isNeededFacet = new Facet<boolean>('isNeeded');
/**
 * Facet which stores the 'activator' callback for a control handler once it has been registered.
 */
const activatorFacet = new Facet<() => void>('activator');

/**
 * 'Addon' which registers the internal operation handler to manage control implementations.
 */
const controlHandlerAddon = createAddon(() => {
    return (dispatch: IItemCacheDispatch) => {
        dispatch(registerOperationHandler(controlTypeKeyFacet, getItemsOperation)(
            createBatchedItemsOperationHandler()(getControlHandlerItems)
        ));
    };
});

/**
 * Payload passed to the action creator for a control handler retriever.
 * @public
 */
export interface IControlHandlerPayload<TKey extends object, TControlType> {
    key: ControlTypeKey<TKey, TControlType>;
}

/**
 * Interface for a 'control handler', which is a creator for an action which
 * provides the implementation for a control.
 * @public
 */
export interface IControlHandler<TKey extends object, TControlType> {
    /**
     * Creates an action which, when dispatched, provides the implementation for a control.
     */
    (payload: IControlHandlerPayload<TKey, TControlType>): IItemCacheThunkAction<() => void>;
}

/**
 * Gets a function to demand a control handler of a given type
 * within a component connected to the item cache.
 * @param controlTypeFacet - The control type which specifies the injection
 * point and the contract.
 *
 * @deprecated Use `itemCache.demandItemFacet` instead to access control handlers within
 * cache-connected components.
 * @public
 */
export function demandControlHandler<TProps extends object, TOverrides>(
    controlTypeFacet: ControlFacet<TProps, TOverrides>
): ((itemCache: IItemCache, itemKey: string | undefined) => IControl<TProps, TOverrides>) {
    return (itemCache: IItemCache, itemKey: string | undefined) => {
        return itemCache.demandItemFacet(controlTypeFacet, itemKey) || controlTypeFacet.default;
    };
}

/**
 * Registers a creator for a control handler for a given control point
 * and key facet.
 * The control handler will be requested the first time an item from the
 * given key facet is consumed in the target control.
 * The control handler will then be invoked whenever controls update
 * with items of the given type.
 * @param keyFacet - The key facet for which the control handler applies.
 * @param controlTypeFacet - The control type which specifies the injection
 * point and the contract.
 * @public
 */
export function registerControlHandler<TKey extends object, TControlType>(
    keyFacet: KeyFacet<TKey>,
    controlTypeFacet: IFacet<TControlType>
): (controlHandler: IControlHandler<TKey, TControlType>) => IItemCacheThunkAction<void> {
    const controlKey = ControlFacet.getControlTypeKey({
        keyFacet: keyFacet,
        controlTypeFacet: controlTypeFacet
    });

    return (controlHandler: IControlHandler<TKey, TControlType>) => {
        return (dispatch: IItemCacheDispatch) => {
            dispatch(controlHandlerAddon);

            const activator = dispatch(controlHandler({
                key: controlKey
            }));

            const isNeeded = dispatch(readItems((itemCache: IItemCache) => itemCache.demandItemFacet(isNeededFacet, controlKey)));

            dispatch(resolveItems({
                items: {
                    [controlKey]: activatorFacet.pack(activator)
                }
            }));

            dispatch(registerPermanentItemDemands({
                items: {
                    [controlKey]: {}
                }
            }));

            if (isNeeded) {
                activator();
            }
        };
    };
}

/**
 * Creates a control handler from the given implementation action creator.
 *
 * This function is invoked with no arguments, and returns a function which is used
 * to pass an action creator for the control implementation.
 * @public
 */
export function createControlHandler(
    /* Reserved for future parameterization */
): <TKey extends object, TControlType>(
        creator: () => IItemCacheThunkAction<TControlType>
    ) => IControlHandler<TKey, TControlType> {
    return <TKey extends object, TControlType>(creator: () => IItemCacheThunkAction<TControlType>) => {
        return (payload: {
            key: ControlTypeKey<TKey, TControlType>;
        }) => {
            const {
                key: controlKey
            } = payload;

            const {
                controlTypeId
            } = controlTypeKeyFacet.deserialize(controlKey);

            return (dispatch: IItemCacheDispatch) => {
                const control = dispatch(creator());

                dispatch(resolveItems({
                    items: {
                        [controlKey]: {
                            ...pack({}, {}),
                            [controlTypeId]: control
                        }
                    }
                }));

                return alreadyActivated;
            };
        };
    };
}

/**
 * Creates a control handler from the given loader for an implementation action creator.
 * @param loadHandler - A callback which, when invoked, returns a promise which resolves
 * to an action creator for a control implementation.
 * @public
 */
export function createDeferredControlHandler<TKey extends object, TControlType>(
    loadHandler: () => Promise<IControlHandler<TKey, TControlType>>
): IControlHandler<TKey, TControlType> {
    return (payload: {
        key: ControlTypeKey<TKey, TControlType>;
    }) => {
        const {
            key
        } = payload;

        return (dispatch: IItemCacheDispatch) => {
            let activator: () => void;
            let loading = false;

            const load = async () => {
                const handler = await loadHandler();

                activator = dispatch(handler(payload));

                loading = false;

                activator();
            };

            return () => {
                if (activator) {
                    activator();
                } else {
                    const isNeeded = dispatch(readItems((itemCache: IItemCache) => itemCache.demandItemFacet(isNeededFacet, key)));

                    if (isNeeded) {
                        if (!loading) {
                            loading = true;

                            load();
                        }
                    }
                }
            };
        };
    };
}

/**
 * Gets the items tracking the registration state of control handlers.
 * When this is invoked, it indicates that something has demanded one or
 * more control handlers of specific types. Thus, they need to be loaded and
 * activated.
 */
function getControlHandlerItems(
    payload: IBatchOperationPayload<typeof controlTypeKeyFacet.type, typeof getItemsOperation.type>
): IOperationHandlerThunkAction {
    const {
        items: requestedItems
    } = payload;

    return async (dispatch: IItemCacheDispatch) => {
        const activators: (() => void)[] = [];

        const itemResolver = new ItemResolver();

        for (const itemKey in requestedItems) {
            if (Object.prototype.hasOwnProperty.call(requestedItems, itemKey)) {
                const {
                    controlTypeId
                } = controlTypeKeyFacet.deserialize(itemKey as Key<typeof controlTypeKeyFacet.type>);

                if (controlTypeId in requestedItems[itemKey].demands) {
                    const activator = dispatch(readItems((itemCache: IItemCache) => itemCache.demandItemFacet(activatorFacet, itemKey)));

                    itemResolver.resolveItems({
                        items: {
                            [itemKey]: isNeededFacet.pack(true)
                        }
                    });

                    if (activator) {
                        activators.push(activator);
                    }
                }
            }
        }

        dispatch(itemResolver.execute());

        for (const activator of activators) {
            activator();
        }
    };
}
